// ФТУЕ.2: Zustand-стор каркаса туториала.
//
// Состояние одного «шага туториала»:
//   - targetSel — CSS-селектор DOM-элемента, на котором фокусируемся.
//     Если null — coach показывается по центру экрана без таргета.
//   - spotlight — рисовать ли затемнение всего экрана + прозрачную "дырку"
//     вокруг таргета (вырезает rect с rounded corners и мягким glow).
//   - hand — показать ли палец-указатель над таргетом (для tap-инструкций).
//   - dragTo — селектор второго элемента: рисует пунктирную стрелку от
//     targetSel к dragTo (для drag-инструкций).
//   - pulse — добавить ли пульсирующую неон-рамку вокруг таргета.
//   - coach — текстовый бабл со стрелкой к таргету (или в центре, если
//     таргета нет). Может содержать title, body, кнопки skip/next.
//   - progress — счётчик «N / TOTAL» вверху экрана (скрыт в туториал-бое
//     чтобы не загромождать поле).
//
// Использование (из компонента-сценария туториала):
//   const start = useTutorialStore(s => s.setStep);
//   start({ id: 'home-pvp', targetSel: '[data-tt="home-pvp-btn"]',
//           spotlight: true, hand: true,
//           coach: { title: 'Найди бой', body: 'Тапни сюда — мы подберём
//                    противника твоего уровня.', dir: 'top',
//                    showNext: false } });
// После выполнения действия пользователем (тап) — компонент-сценарий
// вызывает setStep(nextStep) или setStep(null).

import { create } from 'zustand';

export type CoachDir =
  | 'top' | 'bottom' | 'left' | 'right'
  | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface TutorialCoach {
  title?: string;
  /** Может быть string или JSX (для inline-иконок Скриптов/ШК и пр.). */
  body: import('react').ReactNode;
  dir?: CoachDir;
  showNext?: boolean;
  showSkip?: boolean;
  /** Текст кнопки Next. По умолчанию «Дальше». */
  nextLabel?: string;
}

export interface TutorialStep {
  /** Уникальный id шага — для аналитики и debounce-логики. */
  id: string;
  /** CSS-селектор DOM-элемента, на котором фокусируемся. */
  targetSel?: string;
  /** Если задан — рисуем пунктирную drag-стрелку от targetSel сюда. */
  dragToSel?: string;
  /** Затемнение всего экрана + дырка вокруг таргета. */
  spotlight?: boolean;
  /** Палец-указатель над таргетом. */
  hand?: boolean;
  /** Пульсирующая рамка вокруг таргета. */
  pulse?: boolean;
  /** Цвет неона для дырки/рамки. По умолчанию cy. */
  accent?: 'cy' | 'mg' | 'amber' | 'lime' | 'deus' | 'syn' | 'gsec' | 'liv';
  /** Тёмный бабл с текстом. */
  coach?: TutorialCoach;
  /** Полоса прогресса вверху. Если undefined — скрыта. */
  progress?: { current: number; total: number };
  /** Можно ли продвинуться кликом на сам таргет (не на «Дальше»). */
  advanceOnTargetClick?: boolean;
}

interface TutorialStore {
  step: TutorialStep | null;
  /** Текущий bounding rect таргета. Обновляется TutorialOverlay при ресайзе. */
  targetRect: DOMRect | null;
  /** Bounding rect для второй точки (drag). */
  dragToRect: DOMRect | null;
  /** Колбэк, который вызовется при клике на Skip. По умолчанию — просто setStep(null). */
  onSkipCb: (() => void) | null;
  /** Колбэк при клике на Next. По умолчанию — setStep(null) (сценарий сам поставит следующий). */
  onNextCb: (() => void) | null;

  setStep: (step: TutorialStep | null) => void;
  setTargetRect: (rect: DOMRect | null) => void;
  setDragToRect: (rect: DOMRect | null) => void;
  setCallbacks: (cbs: { onNext?: () => void; onSkip?: () => void }) => void;
  /** Запустить шаг с колбэками за один вызов. */
  start: (step: TutorialStep, cbs?: { onNext?: () => void; onSkip?: () => void }) => void;
  stop: () => void;
}

export const useTutorialStore = create<TutorialStore>((set) => ({
  step: null,
  targetRect: null,
  dragToRect: null,
  onSkipCb: null,
  onNextCb: null,

  setStep: (step) => set({ step }),
  setTargetRect: (rect) => set({ targetRect: rect }),
  setDragToRect: (rect) => set({ dragToRect: rect }),
  setCallbacks: ({ onNext, onSkip }) => set({
    onNextCb: onNext || null,
    onSkipCb: onSkip || null
  }),
  start: (step, cbs) => set({
    step,
    onNextCb: cbs?.onNext || null,
    onSkipCb: cbs?.onSkip || null
  }),
  stop: () => set({
    step: null,
    targetRect: null,
    dragToRect: null,
    onNextCb: null,
    onSkipCb: null
  })
}));
